/**
 * offlineRequestQueue.ts
 *
 * Accodamento AUTOMATICO delle scritture fallite per assenza di rete.
 *
 * Quando la connessione cade, `apiFetch` esaurisce i retry e lancia
 * `NetworkError`: fino a oggi l'azione dell'utente andava persa (con un
 * semplice errore a schermo). Qui la richiesta viene accodata come azione
 * `api-request` di `offlineQueue` e rigiocata da `backgroundSync` con
 * `apiFetch` (base URL + token) quando la rete torna.
 *
 * Le richieste NON accodabili restano quelle che non ha senso ripetere a
 * distanza di tempo o che non sono serializzabili in JSON:
 *   - autenticazione (login, registrazione, refresh, reset password);
 *   - pagamenti/checkout (il replay di un pagamento è responsabilità del PSP);
 *   - azioni amministrative (meglio l'errore immediato, non una modifica
 *     applicata a sorpresa minuti dopo);
 *   - upload multipart (FormData/Blob non sopravvivono alla serializzazione).
 *
 * L'header `X-RC-Offline-Replay` marca le richieste generate dal replay: se
 * anche quelle falliscono non vengono ri-accodate (niente loop).
 */

import {
  MAX_QUEUE_SIZE,
  enqueueAction,
  getPendingActions,
  initDB,
  type QueuedAction,
} from './offlineQueue';

/** Marca le richieste originate dal replay offline (anti-loop). */
export const OFFLINE_REPLAY_HEADER = 'X-RC-Offline-Replay';

/** Evento emesso quando una scrittura viene messa in coda (banner/UI). */
export const OFFLINE_QUEUED_EVENT = 'rc:offline-action-queued';

/**
 * Evento emesso a fine replay con i path effettivamente inviati al server:
 * permette a chi legge da cache (locale o React Query) di rileggere i dati.
 */
export const OFFLINE_SYNC_COMPLETED_EVENT = 'rc:offline-sync-completed';

/** Dettaglio dell'evento di accodamento (consumato dal ponte delle cache). */
export interface QueuedRequestDetail {
  method: string;
  path: string;
}

/** Metodi di scrittura: solo questi possono essere riprodotti in sicurezza. */
const WRITE_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

/** Path mai accodabili (auth, pagamenti, admin, upload). */
const NEVER_QUEUE_PREFIXES = [
  '/api/auth/',
  '/api/payments',
  '/api/payment',
  '/api/stripe',
  '/api/checkout',
  '/api/subscription/checkout',
  '/api/admin/',
  '/api/upload',
  '/api/media',
  '/api/offline',
];

/**
 * True se la richiesta è una scrittura su `/api/` che può essere rigiocata
 * più tardi in modo sicuro.
 */
export function isQueueableRequest(
  method: string | undefined,
  path: string,
  headers?: HeadersInit,
): boolean {
  const normalizedMethod = (method ?? 'GET').toUpperCase();
  if (!WRITE_METHODS.has(normalizedMethod)) return false;
  if (!path.startsWith('/api/')) return false;

  if (typeof Headers !== 'undefined' && new Headers(headers).has(OFFLINE_REPLAY_HEADER)) {
    return false;
  }

  const lowerPath = path.toLowerCase();
  return !NEVER_QUEUE_PREFIXES.some((prefix) => lowerPath.startsWith(prefix));
}

/** Chiave di dedup: identifica in modo stabile metodo + path + body. */
export function requestDedupKey(
  method: string,
  path: string,
  body: string | null,
): string {
  return `${method} ${path} ${body ?? ''}`.slice(0, 512);
}

/** Le scritture che l'utente compie di persona hanno priorità maggiore. */
function priorityForPath(path: string): number {
  if (path.startsWith('/api/garage')) return 3;
  if (path.startsWith('/api/profile')) return 3;
  if (path.startsWith('/api/events')) return 4;
  return 5;
}

/**
 * Accoda una scrittura fallita per errore di rete.
 *
 * @returns true se la richiesta è stata realmente messa in coda (o era già
 *          presente come duplicato: in quel caso è comunque salvata), false
 *          se non è accodabile o se IndexedDB non è disponibile.
 */
export async function enqueueFailedRequest(
  path: string,
  init: RequestInit,
): Promise<boolean> {
  const method = (init.method ?? 'GET').toUpperCase();
  if (!isQueueableRequest(method, path, init.headers)) return false;

  // Il body accodabile è solo una stringa JSON già serializzata: FormData,
  // Blob e ReadableStream non sono ricostruibili dopo un riavvio dell'app.
  const body = init.body;
  if (body != null && typeof body !== 'string') return false;
  const rawBody = (body as string | null | undefined) ?? null;

  try {
    if (!(await initDB())) return false;

    const dedupKey = requestDedupKey(method, path, rawBody);
    const pending = await getPendingActions();

    // Doppio tap o retry manuale: la richiesta identica è già in coda.
    if (pending.some((a) => a.data?.dedupKey === dedupKey)) return true;

    if (pending.filter((a) => a.type === 'api-request').length >= MAX_QUEUE_SIZE) {
      console.warn('[OfflineQueue] Limite richieste accodate raggiunto:', path);
      return false;
    }

    await enqueueAction(
      'api-request',
      { method, path, body: rawBody, dedupKey },
      priorityForPath(path),
    );
    console.info(`[OfflineQueue] Scrittura accodata (offline): ${method} ${path}`);
    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent<QueuedRequestDetail>(OFFLINE_QUEUED_EVENT, {
          detail: { method, path },
        }),
      );
    }
    return true;
  } catch (err) {
    console.warn('[OfflineQueue] Accodamento fallito:', err);
    return false;
  }
}

/** Payload salvato nella coda per le azioni `api-request`. */
export interface QueuedApiRequest {
  method: string;
  path: string;
  body: string | null;
  dedupKey?: string;
}

/**
 * Ricostruisce il payload di una `api-request` (usato dal replay).
 * Ritorna null se l'azione non è una richiesta HTTP valida.
 */
export function readQueuedApiRequest(
  action: QueuedAction,
): QueuedApiRequest | null {
  const data = action.data as Partial<QueuedApiRequest> | undefined;
  if (!data || typeof data.path !== 'string' || !data.path.startsWith('/api/')) {
    return null;
  }
  return {
    method: (data.method ?? 'POST').toUpperCase(),
    path: data.path,
    body: typeof data.body === 'string' ? data.body : null,
    dedupKey: data.dedupKey,
  };
}
